import React, { useEffect, useState } from 'react'
import { Button, InputAdornment, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import JelledModal, { ModalButtonsContainerStack } from 'components/JelledModal'
import { isFieldEmpty } from 'utils'
import { useActiveOrganization } from 'hooks/use-organization'
import { authenticatedUserAtom, toastAtom } from 'state'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { TwinNote } from 'api/model/twinNote'
import { twinsCrawlUrl, twinsNewNote, twinsUpdateNote } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { useFetchFullNote } from 'hooks/use-notes'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { getElementColor } from 'utils/darkMode'
import { TextFieldWithDarkMode } from 'dark-mode-components'
import { OnboardingStep, Reference, SearchScopeType } from 'api/model'
import withOnboardingStep from 'components/FTUE/withOnboardingStep'
import { LoadingButton } from '@mui/lab'
import SearchScopeSelector from './VisibilitySelector'
import ReferencesList from 'components/References/ReferencesList'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import LinkIcon from '@mui/icons-material/Link'
import { COLORS } from 'app-constants'

const AddOrEditNoteModal = ({
  noteId,
  onClose,
  onAddNote,
  onEditNote,
  onHydrateFullNote,
  updateOnboardingStep,
}: {
  noteId: string
  onClose: () => void
  onAddNote: (note: TwinNote) => void
  onEditNote: (note: TwinNote) => void
  onHydrateFullNote?: (note: TwinNote) => void
  updateOnboardingStep: () => void
}) => {
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()

  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)
  const [user] = useRecoilState(authenticatedUserAtom)

  const hasSetTwinProfile = !!user?.onboarding_steps_completed.includes(OnboardingStep.set_twin_profile)

  const { fullNote, isLoading: isLoadingFullNote } = useFetchFullNote(noteId)

  useEffect(() => {
    if (fullNote) {
      setTitle(fullNote.title)
      setContent(fullNote.content)
      setUrl(fullNote.url ?? '')
      setSearchScope(fullNote.search_scope ?? SearchScopeType.global)
      setReferences(fullNote.references ?? [])
    }
  }, [fullNote])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitCount, setSubmitCount] = useState(0)

  const [title, setTitle] = useState<string>('')
  const onTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value)
  }

  const [url, setUrl] = useState<string>('')
  const [lastFetchedUrl, setLastFetchedUrl] = useState<string>('')
  const onUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value)
  }

  const [references, setReferences] = useState<Reference[]>([])

  const [isCrawlingURL, setIsCrawlingURL] = useState<boolean>(false)
  const [didFetchURLContent, setDidFetchURLContent] = useState<boolean>(false)

  const [content, setContent] = useState<string>('')
  const onContentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setContent(event.target.value)
  }

  const [searchScope, setSearchScope] = useState<SearchScopeType>(SearchScopeType.global)

  const onAddNewNote = async () => {
    if (!activeOrganization) {
      return null
    }

    try {
      const response = await twinsNewNote(activeOrganization, {
        title,
        content,
        url,
        search_scope: searchScope,
      })

      onAddNote(response.data)

      // if onboarding checklist step for setting twin profile has not been complete it, mark it as complete
      if (!hasSetTwinProfile) {
        updateOnboardingStep()
      }

      setToast({ title: 'Added note', severity: 'success' })
      onClose()
    } catch (error) {
      setToast({ title: 'Failed to add note', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onEditExistingNote = async () => {
    if (!activeOrganization || !fullNote?.id) {
      return null
    }

    try {
      const response = await twinsUpdateNote(activeOrganization, fullNote.id, {
        title,
        content,
        search_scope: searchScope,
        references: references?.length > 0 ? references : undefined,
      })

      onEditNote(response.data)
      onHydrateFullNote?.(response.data)
      setToast({ title: 'Edited note', severity: 'success' })
      onClose()
    } catch (error) {
      setToast({ title: 'Failed to edit note', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onFetchURLContent = async () => {
    if (!activeOrganization || url === '') {
      return
    }

    try {
      setIsCrawlingURL(true)
      const response = await twinsCrawlUrl(activeOrganization, {
        url,
      })
      setContent(response.data.text)

      // we should backfill the title if the user has not yet set the title
      if (!title && response.data.title) {
        setTitle(response.data.title)
      }

      // overwrite existing references with new ones
      if (response.data.references) {
        setReferences(response.data.references)
      }
      setDidFetchURLContent(true)
      setLastFetchedUrl(url)
      setToast({ title: 'Successfully fetched URL content', severity: 'success' })
    } catch (error) {
      setToast({ title: 'Failed to fetch URL content', severity: 'error' })
    } finally {
      setIsCrawlingURL(false)
    }
  }

  const onFormSubmit = async () => {
    setIsSubmitting(true)
    setSubmitCount((submitCount) => submitCount + 1)

    if (!activeOrganization || isFieldEmpty(title) || isFieldEmpty(content)) {
      setIsSubmitting(false)
      return
    }

    if (fullNote) {
      onEditExistingNote()
    } else {
      onAddNewNote()
    }
  }

  const buttonText = () => {
    const isFetchingSameUrl = lastFetchedUrl === url
    const urlHasChangedSinceEdit = fullNote?.url && fullNote?.url !== url
    const noUrlFetched = url === '' && !fullNote

    if (!didFetchURLContent || isFetchingSameUrl || urlHasChangedSinceEdit || noUrlFetched) {
      return 'Fetch content'
    }
    return 'Refetch content'
  }

  if (isLoadingFullNote) {
    return null
  }

  return (
    <JelledModal
      isOpen={true}
      width={isMobile ? '100%' : '960px'}
      modalStyle={{
        backgroundColor: COLORS.PURE_WHITE,
        marginLeft: isMobile ? '16px' : 0,
        marginRight: isMobile ? '16px' : 0,
      }}>
      <Stack>
        <Typography
          mb={4}
          variant="h4"
          color="secondary"
          sx={{
            width: 'fit-content',
          }}>
          {fullNote ? 'Edit note' : 'New note'}
        </Typography>

        <TextFieldWithDarkMode
          className="has-label"
          required
          isDarkBackdrop={false}
          theme={theme}
          label="Title"
          placeholder="Add a descriptive title for your note"
          value={title}
          onChange={onTitleChange}
          disabled={isSubmitting}
          error={submitCount > 0 && isFieldEmpty(title)}
          helperText={submitCount > 0 && isFieldEmpty(title) && 'You must enter a title'}
          sx={{
            marginBottom: '32px',
            '& .MuiFormLabel-root': {
              color: getElementColor(true, theme),
            },
          }}
        />

        <Stack direction="row" alignItems="center" spacing={1} mb={4}>
          <TextFieldWithDarkMode
            className="has-label"
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  sx={{
                    mr: 0,
                  }}>
                  <LinkIcon
                    sx={{
                      height: '18px',
                      width: '18px',
                      fill: COLORS.GRAY_3,
                      color: COLORS.GRAY_3,
                    }}
                  />
                </InputAdornment>
              ),
            }}
            fullWidth
            isDarkBackdrop={false}
            theme={theme}
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5">URL</Typography>

                <Tooltip
                  title="Use a URL to get text from external websites. Clicking ‘Fetch content’ will override existing text in the content box."
                  enterTouchDelay={0}>
                  <InfoOutlined
                    sx={{
                      height: '16px',
                      width: '16px',
                      color: COLORS.GRAY_3,
                    }}
                  />
                </Tooltip>
              </Stack>
            }
            placeholder="https://"
            value={url}
            onChange={onUrlChange}
            disabled={isCrawlingURL}
          />

          <LoadingButton
            disabled={url === '' || isCrawlingURL}
            loading={isCrawlingURL}
            loadingIndicator={
              <Typography
                variant="h6"
                sx={{
                  color: COLORS.PURE_WHITE,
                }}>
                Fetching...
              </Typography>
            }
            variant="contained"
            onClick={onFetchURLContent}
            sx={{
              width: '240px',
              borderWidth: '1px',
              color: COLORS.PURE_WHITE,
            }}>
            {buttonText()}
          </LoadingButton>
        </Stack>

        <TextFieldWithDarkMode
          className="has-label"
          required
          isDarkBackdrop={false}
          theme={theme}
          multiline
          rows={6}
          label="Content"
          placeholder="Add snippets of useful information"
          value={content}
          onChange={onContentChange}
          disabled={isSubmitting}
          error={submitCount > 0 && isFieldEmpty(content)}
          helperText={submitCount > 0 && isFieldEmpty(content) && 'You must enter some content'}
          sx={{
            whiteSpace: 'pre-line',
            mb: 2,
            '& .MuiFormLabel-root': {
              color: getElementColor(true, theme),
            },
          }}
        />

        <SearchScopeSelector
          disabled={false}
          entityName="Note"
          searchScope={searchScope}
          setSearchScope={setSearchScope}
        />

        <ReferencesList references={references} setReferences={setReferences} submitCount={0} />

        <ModalButtonsContainerStack>
          <Button
            size="medium"
            onClick={() => onClose()}
            sx={{
              color: 'primary.main',
            }}>
            Cancel
          </Button>
          <Button variant="contained" size="medium" color="primary" onClick={onFormSubmit}>
            Save
          </Button>
        </ModalButtonsContainerStack>
      </Stack>
    </JelledModal>
  )
}

export default withOnboardingStep(AddOrEditNoteModal, OnboardingStep.set_twin_profile)
